import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useGraph, type Lens } from '../hooks/useGraph'
import { GraphCanvas } from '../graph/GraphCanvas'
import { GraphFilters } from '../graph/GraphFilters'
import { DetailPanel } from '../graph/DetailPanel'
import { LensSwitch } from '../graph/LensSwitch'
import { StatusLegend } from '../graph/StatusLegend'
import { Skeleton } from '../components/ui/Skeleton'
import { useStatusStream } from '../hooks/useStatusStream'

/** The graphical navigator. Landscape → drill-down across two lenses; scope +
 *  filters live in the URL; the operational overlay updates live via SSE. */
export function ExplorePage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const scope = params.get('scope')
  const q = params.get('q') ?? ''
  const health = params.get('health') ?? ''
  const lens: Lens = params.get('lens') === 'deployment' ? 'deployment' : 'logical'
  const [selected, setSelected] = useState<string | null>(null)
  const { data, isLoading, isError } = useGraph(lens, scope)
  useStatusStream() // live overlay: reconciles status deltas into the graph cache

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k)
      else next.set(k, v)
    }
    setParams(next)
  }

  const goTo = (key: string | null) => {
    setSelected(null)
    update({ scope: key })
  }

  const switchLens = (next: Lens) => {
    setSelected(null)
    update({ lens: next === 'logical' ? null : next, scope: null })
  }

  const pivotToDeployment = (nodeKey: string) => {
    setSelected(null)
    update({ lens: 'deployment', scope: nodeKey })
  }

  const filtered = useMemo(() => {
    const nodes = (data?.nodes ?? []).filter((n) => {
      if (health && n.health !== health) return false
      if (q) {
        const needle = q.toLowerCase()
        if (!n.name.toLowerCase().includes(needle) && !n.tags.some((tg) => tg.toLowerCase().includes(needle))) {
          return false
        }
      }
      return true
    })
    const keys = new Set(nodes.map((n) => n.key))
    const edges = (data?.edges ?? []).filter((e) => keys.has(e.from) && keys.has(e.to))
    return { nodes, edges }
  }, [data, q, health])

  const selectedNode = data?.nodes.find((n) => n.key === selected)

  return (
    <section>
      <div className="flex items-center gap-2 text-sm">
        <h2 className="mr-2 text-xl font-semibold">{t('nav.explore')}</h2>
        <nav className="flex items-center gap-1 text-neutral-500" aria-label="breadcrumb">
          <button type="button" className="hover:text-blue-600" onClick={() => goTo(null)}>
            {t('explore.landscape')}
          </button>
          {(data?.breadcrumb ?? []).map((c) => (
            <span key={c.key} className="flex items-center gap-1">
              <span aria-hidden>/</span>
              <button type="button" className="hover:text-blue-600" onClick={() => goTo(c.key)}>
                {c.name}
              </button>
            </span>
          ))}
        </nav>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-4">
        <LensSwitch lens={lens} onChange={switchLens} />
        <GraphFilters health={health} onQuery={(v) => update({ q: v })} onHealth={(v) => update({ health: v })} />
        <StatusLegend />
      </div>

      {isLoading && (
        <div className="mt-4">
          <Skeleton rows={4} columns={3} />
        </div>
      )}
      {isError && <p className="mt-6 text-red-600">{t('explore.error')}</p>}
      {data && filtered.nodes.length === 0 && <p className="mt-6 text-neutral-400">{t('explore.empty')}</p>}
      {data && filtered.nodes.length > 0 && (
        <div className="mt-4 flex gap-4">
          <div className="flex-1">
            <GraphCanvas nodes={filtered.nodes} edges={filtered.edges} onNodeClick={setSelected} />
          </div>
          {selected && (
            <DetailPanel
              elementKey={selected}
              edges={data.edges}
              canDrill={selectedNode?.hasChildren ?? false}
              onDrill={() => goTo(selected)}
              onClose={() => setSelected(null)}
              onPivot={lens === 'logical' ? pivotToDeployment : undefined}
            />
          )}
        </div>
      )}
    </section>
  )
}
